import { cn } from "@-/com.npmjs.cn";
import { View, type ViewProps } from "@-/com.npmjs.react-native";
import { Surface, SurfaceHeader } from "@-/dev.improve.haniverse.components.ui.surface";
import type { ReactNode } from "react";
import { Skeleton, type SkeletonProps } from "#skeleton";

/**
 * Shared building blocks for loading placeholders. They mirror the loaded chrome (a `Surface` with
 * a muted header strip over a 4px-gutter grid of `rounded-xs` tiles) so every skeleton in the app
 * has the same corners, tints and spacing instead of restyling its own approximation.
 */

/** Busy, non-interactive region announced once to assistive tech. */
export function SkeletonGroup({
  accessibilityLabel = "Loading content",
  ...props
}: ViewProps & React.RefAttributes<View>) {
  return (
    <View
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ busy: true }}
      accessible
      pointerEvents="none"
      {...props}
    />
  );
}

/** A square glyph tile; `flex-1` so a row of them splits the width like the loaded grid. */
export function SkeletonTile({ className, ...props }: SkeletonProps) {
  return <Skeleton className={cn("aspect-square min-w-0 flex-1", className)} {...props} />;
}

/** `rows` × `columns` glyph tiles with the loaded grid's 4px gutters and 4px card inset. */
export function SkeletonTileGrid({
  className,
  columns = 8,
  rows = 2,
}: {
  className?: string;
  columns?: number;
  rows?: number;
}) {
  return (
    <View className={cn("gap-1 p-1", className)}>
      {Array.from({ length: rows }, (_, row) => (
        <View className="flex-row gap-1" key={row}>
          {Array.from({ length: columns }, (_, column) => (
            <SkeletonTile key={column} />
          ))}
        </View>
      ))}
    </View>
  );
}

/** A `Badge` pill (`text-xs` + `py-0.5` + border) — the one placeholder that is fully rounded. */
export function SkeletonBadge({ className, ...props }: SkeletonProps) {
  return <Skeleton className={cn("h-5.5 w-20 rounded-full", className)} {...props} />;
}

/** `SurfaceHeader` strip: a `SurfaceTitle` line over an optional `SurfaceDescription` line. */
export function SkeletonSurfaceHeader({
  className,
  description = true,
  leading,
  titleClassName,
  trailing,
}: {
  className?: string;
  /** Omit for headers that only carry a title. */
  description?: boolean;
  leading?: ReactNode;
  titleClassName?: string;
  trailing?: ReactNode;
}) {
  return (
    <SurfaceHeader className={cn("flex-row items-center gap-3 py-3", className)}>
      {leading}
      <View className="min-w-0 flex-1 gap-2">
        <Skeleton className={cn("h-4 w-20", titleClassName)} />
        {description && <Skeleton className="h-3 w-32" />}
      </View>
      {trailing}
    </SurfaceHeader>
  );
}

/** A `Surface` card with a header strip above a glyph-tile grid (the Levels list card). */
export function SkeletonTileSurface({
  className,
  columns,
  gridClassName,
  header = <SkeletonSurfaceHeader />,
  rows,
}: {
  className?: string;
  columns?: number;
  gridClassName?: string;
  header?: ReactNode;
  rows?: number;
}) {
  return (
    <Surface className={className}>
      {header}
      <SkeletonTileGrid className={gridClassName} columns={columns} rows={rows} />
    </Surface>
  );
}

/** `SectionAccordion` sticky header: 16px icon, `text-base` title and chevron on a muted strip. */
export function SkeletonAccordionHeader({ titleClassName }: { titleClassName?: string }) {
  return (
    <View className="border-b border-border bg-muted">
      <View className="mx-2.25 h-9 flex-row items-center gap-2">
        <Skeleton className="size-4" />
        <Skeleton className={cn("h-4 w-24", titleClassName)} />
        <View className="flex-1" />
        <Skeleton className="size-4.5" />
      </View>
    </View>
  );
}

/** One accordion section; pass `children` to draw it expanded with its `bg-surface` body. */
export function SkeletonAccordionSection({
  children,
  titleClassName,
}: {
  children?: ReactNode;
  titleClassName?: string;
}) {
  return (
    <View>
      <SkeletonAccordionHeader titleClassName={titleClassName} />
      {children !== undefined && (
        <View className="border-b border-border bg-surface">{children}</View>
      )}
    </View>
  );
}
